import React, { useState, useEffect } from "react";
import {
    collection,
    doc,
    onSnapshot,
    updateDoc,
    getDocs,
    addDoc,
    query,
} from "firebase/firestore";
import db from "./Firebase";
import { useNavigate, Link } from "react-router-dom";

function EditCoursePage() {
    const [courses, setCourses] = useState([]);
    const [selectedCourseId, setSelectedCourseId] = useState("");
    const [originalData, setOriginalData] = useState(null);
    const [formState, setFormState] = useState(null);
    const [email, setEmail] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        const storedUsername = localStorage.getItem("email");
        const storedPassword = localStorage.getItem("password");
        const storedRemember = localStorage.getItem("remember");

        setEmail(storedUsername);

        if (storedRemember === "false" || !storedUsername || !storedPassword) {
            navigate('/signin');
        }


    }, []);

    // 1. Load all courses
    useEffect(() => {
        const unsub = onSnapshot(collection(db, "nv_courses"), (snap) => {
            const arr = snap.docs.map((d) => ({
                id: d.id,
                data: d.data(),
            }));
            setCourses(arr);
        });
        return () => unsub();
    }, []);

    // 2. When a course is selected, subscribe to its doc
    useEffect(() => {
        if (!selectedCourseId) {
            setOriginalData(null);
            setFormState(null);
            return;
        }
        const docRef = doc(db, "nv_courses", selectedCourseId);
        const unsub = onSnapshot(docRef, (snap) => {
            if (snap.exists()) {
                const d = snap.data();
                setOriginalData(d);
                // Deep clone to avoid mutating original
                setFormState(JSON.parse(JSON.stringify(d)));
            } else {
                setOriginalData(null);
                setFormState(null);
            }
        });
        return () => unsub();
    }, [selectedCourseId]);

    if (!formState) {
        return (
            <div className="edit-course-page">
                <h2>Edit Course</h2>
                <div>
                    <label>Select Course:</label>

                    <select
                        className="form-control"
                        value={selectedCourseId}
                        onChange={(e) => setSelectedCourseId(e.target.value)}
                    >
                        <option value="">-- Select a course --</option>
                        {courses.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.data.course_name || c.id}
                            </option>
                        ))}

                    </select>
                </div>
            </div>
        );
    }

    // Handler for top-level fields
    const handleChange = (field, value) => {
        setFormState((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    // Nested: course_price
    const handlePriceChange = (key, value) => {
        setFormState((prev) => ({
            ...prev,
            course_price: {
                ...(prev.course_price || {}),
                [key]: value,
            },
        }));
    };

    // Lessons handling
    const handleLessonChange = (index, field, value) => {
        const lessons = formState.lessons ? [...formState.lessons] : [];
        lessons[index] = {
            ...lessons[index],
            [field]: value,
        };
        setFormState((prev) => ({
            ...prev,
            lessons,
        }));
    };

    const addLesson = () => {
        setFormState((prev) => {
            const lessons = prev.lessons ? [...prev.lessons] : [];
            lessons.push({
                title: "",
                lesson: "",
                description: "",
                start_on: "",
                end_on: "",
                completion_percentage: "0%",
                is_completed: "no",
                is_file: "no",
                is_online: "yes",
                is_video: "no",
                video_link: "",
                zoom_link: "",
                zoom_date_time: "",
                sequence: lessons.length + 1,
                notes: "",
                timestamp: [],
                worksheet: "",
            });
            return { ...prev, lessons };
        });
    };

    const removeLesson = (index) => {
        setFormState((prev) => {
            const lessons = prev.lessons ? [...prev.lessons] : [];
            lessons.splice(index, 1);
            return { ...prev, lessons };
        });
    };

    // On submit: compute diff and update
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!originalData) return;

        const updates = {};

        // Compare simple fields (excluding lessons)
        for (const key in formState) {
            if (key === "lessons" || key === "course_price") continue;

            const newVal = formState[key];
            const oldVal = originalData[key];
            if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
                updates[key] = newVal;
            }
        }

        // Price
        if (
            JSON.stringify(formState.course_price) !==
            JSON.stringify(originalData.course_price)
        ) {
            updates.course_price = formState.course_price;
        }

        // Lessons
        if (JSON.stringify(formState.lessons) !== JSON.stringify(originalData.lessons)) {
            updates.lessons = formState.lessons;
        }

        if (Object.keys(updates).length === 0) {
            alert("No changes made.");
            return;
        }

        try {
            const docRef = doc(db, "nv_courses", selectedCourseId);
            await updateDoc(docRef, updates);
            alert("Course updated successfully!");
        } catch (err) {
            console.error("Update failed:", err);
            alert("Error updating course. Check console.");
        }

        const logEntry = {
            created_date: new Date(),
            email: email || "unknown",
            page: "EnrollCourse",
            action: `Edited for course ${originalData.course_name || selectedCourseId}}`,
        };
        await addDoc(collection(db, "nv_backend_logs"), logEntry);
        console.log("Log entry added:", logEntry);
    };


    function toDateTimeLocalString(input) {
        try {
            let date;
            if (input?.seconds) {
                // Firestore timestamp
                date = new Date(input.seconds * 1000);
            } else if (input instanceof Date) {
                date = input;
            } else if (typeof input === "string" || typeof input === "number") {
                date = new Date(input);
            }

            if (!date || isNaN(date.getTime())) return "";
            return date.toISOString().slice(0, 16); // 'YYYY-MM-DDTHH:mm'
        } catch (err) {
            return "";
        }
    }



    return (
        <div className="edit-course-page">
            <h2>Edit Course</h2>

            <div>
                <label>Select Course:</label>
                <select
                    value={selectedCourseId}
                    className="form-control"
                    onChange={(e) => setSelectedCourseId(e.target.value)}
                >
                    <option value="">-- Select --</option>
                    {courses.map((c) => (
                        <option key={c.id} value={c.id}>
                            {c.data.course_name || c.id}
                        </option>
                    ))}
                </select>
            </div>

            <form onSubmit={handleSubmit} className="course-form">
                <div className="form-section">
                    <h3>General Info</h3>

                    <label>Course Name</label>
                    <input
                        type="text"
                        value={formState.course_name || ""}
                        onChange={(e) => handleChange("course_name", e.target.value)}
                    />

                    <label>Course ID</label>
                    <input
                        type="text"
                        value={formState.course_id || ""}
                        onChange={(e) => handleChange("course_id", e.target.value)}
                    />

                    <label>Course Description</label>
                    <textarea
                        value={formState.course_description || ""}
                        onChange={(e) => handleChange("course_description", e.target.value)}
                    />

                    <label>Content Description</label>
                    <input
                        type="text"
                        value={formState.content_description || ""}
                        onChange={(e) =>
                            handleChange("content_description", e.target.value)
                        }
                    />

                    <label>Course Categories</label>
                    <input
                        type="text"
                        value={formState.course_categories || ""}
                        onChange={(e) =>
                            handleChange("course_categories", e.target.value)
                        }
                    />

                    <label>Course Tag</label>
                    <input
                        type="text"
                        value={formState.course_tag || ""}
                        onChange={(e) => handleChange("course_tag", e.target.value)}
                    />

                    <label>Course Type</label>
                    <input
                        type="text"
                        value={formState.course_type || ""}
                        onChange={(e) => handleChange("course_type", e.target.value)}
                    />

                    <label>Course Language</label>
                    <input
                        type="text"
                        value={formState.course_language || ""}
                        onChange={(e) => handleChange("course_language", e.target.value)}
                    />

                    <label>Course Length</label>
                    <input
                        type="text"
                        value={formState.course_length || ""}
                        onChange={(e) => handleChange("course_length", e.target.value)}
                    />

                    <label>Expert Name</label>
                    <input
                        type="text"
                        value={formState.course_expert_name || ""}
                        onChange={(e) =>
                            handleChange("course_expert_name", e.target.value)
                        }
                    />

                    <label>Expert ID</label>
                    <input
                        type="text"
                        value={formState.expert_id || ""}
                        onChange={(e) => handleChange("expert_id", e.target.value)}
                    />

                    <label>Expert Image URL</label>
                    <input
                        type="url"
                        value={formState.expert_image_url || ""}
                        onChange={(e) =>
                            handleChange("expert_image_url", e.target.value)
                        }
                    />

                    <label>Demo Video URL</label>
                    <input
                        type="text"
                        value={formState.demo_video_url || ""}
                        onChange={(e) => handleChange("demo_video_url", e.target.value)}
                    />

                    <label>Course Image URL</label>
                    <input
                        type="url"
                        value={formState.course_image || ""}
                        onChange={(e) => handleChange("course_image", e.target.value)}
                    />

                    <label>Start Date</label>
                    <input
                        type="date"
                        value={
                            formState.course_start_date
                                ? formState.course_start_date.split("T")[0]
                                : ""
                        }
                        onChange={(e) =>
                            handleChange("course_start_date", e.target.value)
                        }
                    />

                    <label>End Date</label>
                    <input
                        type="date"
                        value={
                            formState.course_end_date
                                ? formState.course_end_date.split("T")[0]
                                : ""
                        }
                        onChange={(e) =>
                            handleChange("course_end_date", e.target.value)
                        }
                    />
                </div>

                <div className="form-section">
                    <h3>Booleans / Flags</h3>
                    {[
                        "all_course_notes",
                        "is_certificate",
                        "is_demo_video",
                        "is_doubt_clearing_session",
                        "is_pdfnotes",
                        "is_quiz",
                        "is_recording",
                        "is_whatsappgroup",
                        "pin",
                    ].map((flag) => (
                        <div key={flag} className="flag-row">
                            <label>{flag}</label>
                            <select
                                value={
                                    typeof formState[flag] === "boolean"
                                        ? formState[flag]
                                            ? "true"
                                            : "false"
                                        : formState[flag] || ""
                                }
                                onChange={(e) => {
                                    let v = e.target.value;
                                    // convert to proper type
                                    if (v === "true") v = true;
                                    else if (v === "false") v = false;
                                    handleChange(flag, v);
                                }}
                            >
                                <option value="">--</option>
                                <option value="true">true</option>
                                <option value="false">false</option>
                                <option value="yes">yes</option>
                                <option value="no">no</option>
                            </select>
                        </div>
                    ))}
                </div>

                <div className="form-section">
                    <h3>Course Price</h3>
                    <label>Actual Price</label>
                    <input
                        type="number"
                        value={formState.course_price?.actual_price || ""}
                        onChange={(e) =>
                            handlePriceChange("actual_price", Number(e.target.value))
                        }
                    />

                    <label>Membership Price</label>
                    <input
                        type="number"
                        value={formState.course_price?.membership_price || ""}
                        onChange={(e) =>
                            handlePriceChange("membership_price", Number(e.target.value))
                        }
                    />
                </div>

                <div className="form-section">
                    <h3>Other Fields</h3>
                    <label>Index</label>
                    <input
                        type="number"
                        value={formState.index || ""}
                        onChange={(e) => handleChange("index", Number(e.target.value))}
                    />

                    <label>Passing Marks</label>
                    <input
                        type="number"
                        value={formState.passing_marks || ""}
                        onChange={(e) =>
                            handleChange("passing_marks", Number(e.target.value))
                        }
                    />

                    <label>Quiz ID</label>
                    <input
                        type="text"
                        value={formState.quiz_id || ""}
                        onChange={(e) => handleChange("quiz_id", e.target.value)}
                    />

                    <label>Total Quiz Questions</label>
                    <input
                        type="number"
                        value={formState.total_quiz_questions || ""}
                        onChange={(e) =>
                            handleChange("total_quiz_questions", Number(e.target.value))
                        }
                    />
                </div>

                <div className="form-section">
                    <h3>Lessons</h3>
                    {formState.lessons?.map((lesson, idx) => {
                        console.log("lesson.start_on:", lesson.start_on);
                        return (
                            <div key={lesson.sequence || idx} className="lesson-block">
                                <h4>Lesson {idx + 1}</h4>

                                <label>Title</label>
                                <input
                                    type="text"
                                    value={lesson.title || ""}
                                    onChange={(e) =>
                                        handleLessonChange(idx, "title", e.target.value)
                                    }
                                />

                                <label>Lesson</label>
                                <input
                                    type="text"
                                    value={lesson.lesson || ""}
                                    onChange={(e) =>
                                        handleLessonChange(idx, "lesson", e.target.value)
                                    }
                                />

                                <label>Description</label>
                                <textarea
                                    value={lesson.description || ""}
                                    onChange={(e) =>
                                        handleLessonChange(idx, "description", e.target.value)
                                    }
                                />

                                <label>Start On</label>
                                <input
                                    type="datetime-local"
                                    value={toDateTimeLocalString(lesson.start_on)}
                                    onChange={(e) =>
                                        handleLessonChange(idx, "start_on", new Date(e.target.value))
                                    }
                                />

                                <label>End On</label>
                                <input
                                    type="datetime-local"
                                    value={toDateTimeLocalString(lesson.end_on)}
                                    onChange={(e) =>
                                        handleLessonChange(idx, "end_on", new Date(e.target.value))
                                    }
                                />





                                <label>Zoom Date & Time</label>
                                <input
                                    type="text"
                                    value={lesson.zoom_date_time || ""}
                                    onChange={(e) =>
                                        handleLessonChange(idx, "zoom_date_time", e.target.value)
                                    }
                                />

                                <label>Zoom Link</label>
                                <input
                                    type="url"
                                    value={lesson.zoom_link || ""}
                                    onChange={(e) =>
                                        handleLessonChange(idx, "zoom_link", e.target.value)
                                    }
                                />

                                <label>Video Link</label>
                                <input
                                    type="text"
                                    value={lesson.video_link || ""}
                                    onChange={(e) =>
                                        handleLessonChange(idx, "video_link", e.target.value)
                                    }
                                />

                                <label>Notes</label>
                                <input
                                    type="text"
                                    value={lesson.notes || ""}
                                    onChange={(e) =>
                                        handleLessonChange(idx, "notes", e.target.value)
                                    }
                                />

                                <label>Sequence</label>
                                <input
                                    type="number"
                                    value={lesson.sequence || idx + 1}
                                    onChange={(e) =>
                                        handleLessonChange(idx, "sequence", Number(e.target.value))
                                    }
                                />

                                <button
                                    type="button"
                                    onClick={() => removeLesson(idx)}
                                    className="btn-remove-lesson"
                                >
                                    Remove Lesson
                                </button>
                            </div>
                        )
                    })}
                    <button type="button" onClick={addLesson} className="btn-add-lesson">
                        + Add Lesson
                    </button>
                </div>

                <div className="form-section">
                    <button type="submit" className="btn-save">
                        Save Changes
                    </button>
                </div>
            </form>
        </div>
    );
}

export default EditCoursePage;
